import React, { useState, useEffect, Fragment } from 'react';
import { Grid, Paper, MenuItem, TextField, CircularProgress, Typography, Zoom } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Plot from 'react-plotly.js';
import { authFetch, logout } from '../../auth';
import * as localeDictionary from 'plotly.js/lib/locales/es.js';

const useStyles = makeStyles((theme) => ({
    dashboardRoot: { 
        width: '100%', 
        padding: theme.spacing(1), 
        boxSizing: 'border-box' 
    },
    filterPaper: { 
        padding: theme.spacing(2), 
        marginBottom: theme.spacing(2), 
        backgroundColor: theme.palette.secondary.light 
    },
    chartPaper: { 
        padding: theme.spacing(2), 
        width: '100%', 
        height: '42vh', 
        overflow: 'hidden', 
        display: 'flex', 
        flexDirection: 'column' 
    },
    chartTitle: { 
        fontWeight: 'bold', 
        color: theme.palette.primary.main, 
        marginBottom: theme.spacing(1), 
        fontFamily: 'Asap Condensed', 
        fontSize: 14 
    }
}));

const mapeoMesesCompletos = {
    '01': 'Enero', '02': 'Febrero', '03': 'Marzo', '04': 'Abril',
    '05': 'Mayo', '06': 'Junio', '07': 'Julio', '08': 'Agosto',
    '09': 'Septiembre', '10': 'Octubre', '11': 'Noviembre', '12': 'Diciembre'
};

export default function SmartEnergyDashboard(props) {
    const classes = useStyles();
    const theme = useTheme();
    const [loading, setLoading] = useState(false);

    const [year, setYear] = useState(2025);
    const [month, setMonth] = useState(9);
    const [market, setMarket] = useState('ANTIOQUIA');
    const [dashData, setDashData] = useState(null);

    useEffect(() => {
        setLoading(true);
        const url = `/api/smart-dashboard-data?username=${props.username}&year=${year}&month=${month}&market=${market}`;
        authFetch(url)
            .then(res => res.status === 422 ? logout() : res.json())
            .then(response => {
                if (response) setDashData(response);
                setLoading(false);
            }).catch(() => setLoading(false));
    }, [year, month, market, props.username]);

    if (loading || !dashData) {
        return (
            <Grid container justify="center" alignItems="center" style={{ height: '60vh' }}>
                <CircularProgress />
            </Grid>
        );
    }

    // ==========================================
    // 1. CONFIGURACIÓN GRÁFICO 1: BURBUJAS (CONTROL DE REACTIVA)
    // ==========================================
    const bubbleTrace = {
        x: dashData.bubble_chart.map(b => b.active_energy_mwh),
        y: dashData.bubble_chart.map(b => b.reactive_pen_kvarh),
        text: dashData.bubble_chart.map(b => b.client_name),
        mode: 'markers',
        marker: {
            size: dashData.bubble_chart.map(b => Math.max(b.total_billed_cop / 8000000, 12)),
            color: dashData.bubble_chart.map(b => b.months_in_excess > 6 ? '#EF4444' : b.months_in_excess > 0 ? '#F59E0B' : '#10B981'),
            opacity: 0.75,
            line: { width: 1.5, color: '#fff' }
        },
        hovertemplate: '<b>%{text}</b><br>Activa: %{x} MWh<br>Exceso Reactiva: %{y} kVArh<extra></extra>'
    };

    // ==========================================
    // 2. CONFIGURACIÓN GRÁFICO 2: COSTO MARGINAL CON MWH INYECTADOS
    // ==========================================
    const xPeriods = dashData.time_series.map(t => {
        if (t.periodo && t.periodo.includes('-')) {
            const [ano, mes] = t.periodo.split('-');
            return mapeoMesesCompletos[mes] || t.periodo; // Retorna el mes en texto
        }
        return t.periodo; // Fallback si no viene con guión
    });
    const trendTraces = [
        { 
            x: xPeriods, 
            y: dashData.time_series.map(t => t.costo_min), 
            customdata: dashData.time_series.map(t => t.mwh_min), // Inyección física
            type: 'bar', name: '$ Valle (Mínima)', marker: { color: '#0EA5E9' },
            hovertemplate: '<b>Valor Valle:</b> %{y:,.2f} M COP<br><b>Consumo:</b> %{customdata:,.2f} MWh<extra></extra>'
        },
        { 
            x: xPeriods, 
            y: dashData.time_series.map(t => t.costo_med), 
            customdata: dashData.time_series.map(t => t.mwh_med), // Inyección física
            type: 'bar', name: '$ Resto (Media)', marker: { color: '#6366F1' },
            hovertemplate: '<b>Valor Media:</b> %{y:,.2f} M COP<br><b>Consumo:</b> %{customdata:,.2f} MWh<extra></extra>'
        },
        { 
            x: xPeriods, 
            y: dashData.time_series.map(t => t.costo_max), 
            customdata: dashData.time_series.map(t => t.mwh_max), // Inyección física
            type: 'bar', name: '$ Punta (Máxima)', marker: { color: '#F43F5E' },
            hovertemplate: '<b>Valor Punta:</b> %{y:,.2f} M COP<br><b>Consumo:</b> %{customdata:,.2f} MWh<extra></extra>'
        },
        {
            x: xPeriods, y: dashData.time_series.map(t => t.cu_promedio_mercado),
            type: 'scatter', mode: 'lines+markers', name: 'CU ($/kWh)',
            yaxis: 'y2', line: { color: '#F59E0B', width: 3.5, shape: 'spline' },
            marker: { size: 7, color: '#D97706' },
            hovertemplate: '<b>CU Ponderado:</b> %{y:.2f} $/kWh<extra></extra>'
        }
    ];

    // ==========================================
    // 3. CONFIGURACIÓN GRÁFICO 3: TORTA DE IMPUESTOS (CORREGIDO Y ACTIVO)
    // ==========================================
    const pieTrace = {
        labels: dashData.taxes_pie.map(p => p.concepto),
        values: dashData.taxes_pie.map(p => p.valor),
        type: 'pie',
        hole: 0.45,
        marker: { colors: ['#6366F1', '#10B981', '#F59E0B'] },
        textinfo: 'percent+label',
        hovertemplate: '<b>%{label}</b><br>Recaudado: %{value:,.2f} COP<extra></extra>'
    };

    // ==========================================
    // 4. CONFIGURACIÓN GRÁFICO 4: HEATMAP DE CARGA HORARIA
    // ==========================================
    const heatmapTrace = {
        z: dashData.heatmap_data,
        x: Array.from({ length: 24 }, (_, i) => `${i}:00`),
        y: Array.from({ length: 31 }, (_, i) => `Día ${i + 1}`),
        type: 'heatmap',
        colorscale: 'YlGnBu',
        hovertemplate: 'Hora: %{x}<br>%{y}<br>Demanda: %{z:.2f} kW<extra></extra>'
    };

    // ==========================================
    // 5. CONFIGURACIÓN GRÁFICO 5: DISTRIBUCIÓN DE NIVELES (1 A 4 COMPLETO)
    // ==========================================
    const segmentationTrace = {
        x: dashData.segmentation_bar.map(s => s.total_cop),
        y: dashData.segmentation_bar.map(s => s.nivel),
        type: 'bar',
        orientation: 'h',
        marker: { 
            color: ['#6366F1', '#10B981', '#06B6D4', '#8B5CF6'], // Agregado color para el nivel 4
            opacity: 0.85 
        },
        hovertemplate: '<b>%{y}</b><br>Facturado: %{x:,.2f} COP<extra></extra>'
    };

    const commonLayout = (titleY2 = null) => ({
        autosize: true,
        margin: { l: 50, r: titleY2 ? 50 : 15, t: 15, b: 35 },
        hovermode: 'closest',
        font: { family: 'Asap Condensed', size: 11 },
        paper_bgcolor: theme.palette.background.default,
        plot_bgcolor: theme.palette.background.default,
        legend: { orientation: 'h', y: -0.15, x: 0 },
        barmode: 'stack',
        bargap: 0.55,
        ...(titleY2 && {
            yaxis2: {
                title: '$/kWh',
                overlaying: 'y',
                side: 'right',
                gridcolor: 'rgba(0,0,0,0)'
            }
        })
    });

    return (
        <div className={classes.dashboardRoot}>
            <Zoom in timeout={600}>
                <Grid container spacing={2}>
                    
                    {/* CONTROLES SUPERIORES */}
                    <Grid item xs={12}>
                        <Paper className={classes.filterPaper} elevation={0} variant="outlined">
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <TextField select label="Mercado / Operador" value={market} onChange={(e) => setMarket(e.target.value)} fullWidth size="small">
                                        <MenuItem value="ANTIOQUIA">Submercado Antioquia</MenuItem>
                                        <MenuItem value="BOGOTA - CUNDINAMARCA">Submercado Bogotá - Cundinamarca</MenuItem>
                                        <MenuItem value="TOLIMA">Submercado Tolima</MenuItem>
                                        <MenuItem value="AFINIA">Submercado AFINIA (Costa Norte)</MenuItem>
                                        <MenuItem value="CALI - YUMBO - PUERTO TEJADA">Submercado Valle del Cauca</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={2}>
                                    <TextField select label="Año" value={year} onChange={(e) => setYear(e.target.value)} fullWidth size="small">
                                        <MenuItem value={2024}>2024</MenuItem>
                                        <MenuItem value={2025}>2025</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={2}>
                                    <TextField select label="Mes" value={month} onChange={(e) => setMonth(e.target.value)} fullWidth size="small">
                                        <MenuItem value={2}>Febrero</MenuItem>
                                        <MenuItem value={5}>Mayo</MenuItem>
                                        <MenuItem value={7}>Julio</MenuItem>
                                        <MenuItem value={9}>Septiembre</MenuItem>
                                    </TextField>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* GRÁFICO 1: BURBUJAS DE PENALIZACIÓN */}
                    <Grid item xs={12} md={6}>
                        <Paper className={classes.chartPaper} elevation={1}>
                            <Typography className={classes.chartTitle}>Alerta CREG 015: Gravedad de Excesos Reactivos vs Consumo Activo</Typography>
                            <Plot data={[bubbleTrace]} layout={commonLayout()} useResizeHandler style={{ width: '100%', height: '100%' }} config={{ displayModeBar: false }} />
                        </Paper>
                    </Grid>

                    {/* GRÁFICO 2: TENDENCIA MIXTA CON MWH */}
                    <Grid item xs={12} md={6}>
                        <Paper className={classes.chartPaper} elevation={1}>
                            <Typography className={classes.chartTitle}>Análisis Marginal: Costo Indexado por Franjas ($M COP) vs Comportamiento del Costo Unitario ($/kWh)</Typography>
                            <Plot data={trendTraces} layout={commonLayout(true)} useResizeHandler style={{ width: '100%', height: '100%' }} config={{ displayModeBar: false }} />
                        </Paper>
                    </Grid>

                    {/* GRÁFICO 3: TORTA DE RECAUDO DE IMPUESTOS */}
                    <Grid item xs={12} md={4}>
                        <Paper className={classes.chartPaper} elevation={1}>
                            <Typography className={classes.chartTitle}>Distribución de Impuestos y Recaudos de Terceros (APL / IS)</Typography>
                            <Plot data={[pieTrace]} layout={commonLayout()} useResizeHandler style={{ width: '100%', height: '100%' }} config={{ displayModeBar: false }} />
                        </Paper>
                    </Grid>

                    {/* GRÁFICO 4: HEATMAP DE COMPORTAMIENTO HORARIO */}
                    <Grid item xs={12} md={4}>
                        <Paper className={classes.chartPaper} elevation={1}>
                            <Typography className={classes.chartTitle}>Mapa Horario CREG: Matriz de Demanda de Carga Promedio (kW)</Typography>
                            <Plot data={[heatmapTrace]} layout={{ ...commonLayout(), margin: { l: 45, r: 10, t: 15, b: 35 } }} useResizeHandler style={{ width: '100%', height: '100%' }} config={{ displayModeBar: false }} />
                        </Paper>
                    </Grid>

                    {/* GRÁFICO 5: DISTRIBUCIÓN POR NIVEL DE TENSIÓN DE 1 A 4 */}
                    <Grid item xs={12} md={4}>
                        <Paper className={classes.chartPaper} elevation={1}>
                            <Typography className={classes.chartTitle}>Estructura de la Demanda: Concentración de Cartera por Nivel de Tensión CREG</Typography>
                            <Plot data={[segmentationTrace]} layout={{ ...commonLayout(), yaxis: { automargin: true, fixedrange: true } }} useResizeHandler style={{ width: '100%', height: '100%' }} config={{ displayModeBar: false }} />
                        </Paper>
                    </Grid>

                </Grid>
            </Zoom>
        </div>
    );
}